<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'SLA Detalhado',
  },
});

const byPolicy = computed(() => props.data?.by_policy || []);
const byInbox = computed(() => props.data?.by_inbox || []);
const byAgent = computed(() => props.data?.by_agent || []);

function getComplianceColor(rate) {
  if (rate >= 90) return 'text-n-teal-11';
  if (rate >= 80) return 'text-n-blue-11';
  if (rate >= 70) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getComplianceBgColor(rate) {
  if (rate >= 90) return 'bg-n-teal-9';
  if (rate >= 80) return 'bg-n-blue-9';
  if (rate >= 70) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-shield-check text-lg text-n-teal-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="text-2xl font-bold"
          :class="getComplianceColor(data.compliance_rate)"
        >
          {{ data.compliance_rate || 0 }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-4 gap-3 text-center">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-slate-12">
            {{ data.total_tracked || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Total</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-teal-11">
            {{ data.hit || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Cumpridos</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-ruby-11">
            {{ data.missed || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Violados</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-blue-11">
            {{ data.active || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Ativos</div>
        </div>
      </div>

      <!-- By Policy -->
      <div v-if="byPolicy.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Por Política SLA</span>
        <div class="flex flex-col gap-2">
          <div
            v-for="policy in byPolicy"
            :key="policy.policy_name"
            class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">{{
                policy.policy_name
              }}</span>
              <span
                class="text-sm font-bold"
                :class="getComplianceColor(policy.compliance_rate)"
              >
                {{ policy.compliance_rate }}%
              </span>
            </div>
            <div class="flex items-center gap-2">
              <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
                <div
                  class="h-full transition-all duration-300"
                  :class="getComplianceBgColor(policy.compliance_rate)"
                  :style="{ width: `${policy.compliance_rate}%` }"
                />
              </div>
              <span class="text-xs text-n-slate-11">{{ policy.hit }}/{{ policy.total }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Inboxes -->
      <div v-if="byInbox.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Por Canal</span>
        <div class="flex flex-col gap-1">
          <div
            v-for="inbox in byInbox.slice(0, 5)"
            :key="inbox.inbox_id"
            class="flex items-center justify-between py-1 px-2 rounded bg-n-alpha-1"
          >
            <span class="text-sm text-n-slate-12">{{ inbox.inbox_name }}</span>
            <div class="flex items-center gap-2">
              <span
                class="text-sm font-medium"
                :class="getComplianceColor(inbox.compliance_rate)"
              >
                {{ inbox.compliance_rate }}%
              </span>
              <span class="text-xs text-n-slate-10">({{ inbox.hit }}/{{ inbox.total }})</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Breach Analysis -->
      <div
        v-if="data.breach_analysis?.total_breaches > 0"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-alert-triangle text-n-ruby-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ data.breach_analysis.total_breaches }} violações no período
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
